import {
  ILanguages,
  IListingPhotos,
  IObjectsHasTags,
  ISuitesHasPhotos,
} from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, Equal } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IEditImage,
  IModifyPayload,
  subsetObject,
} from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { getNewListingPhoto } from '@/shared/factory/new-listing-photo';
import uploads from '@/shared/services/uploads';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getByModelAndObjectId } from '@/shared/services/tags';
import { getNewSuitesHasPhotos } from '@/shared/factory/new-suits-has-photo';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/units/:param/assigned-photos';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
  where: [Equal('active', '1')],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};
const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'photos',
  keys: ['name', 'alt_text'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISuitesHasPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

export interface IListingPhotosWithExtraData extends ISuitesHasPhotos {
  languages: ILanguages[];
  objectsHasTags: IObjectsHasTags[];
}

export interface ICustomListingPhotoState extends IStateStrict<ISuitesHasPhotos> {
  singleWithExtraData: IListingPhotosWithExtraData | null;
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    singleWithExtraData: null,
  } as ICustomListingPhotoState,

  getters: {
    ...base.getters,
    singleWithExtraData: (state: ICustomListingPhotoState) => state.singleWithExtraData,
  },

  mutations: {
    ...base.mutations,

    setSingleWithExtraData: (state: ICustomListingPhotoState, payload: IListingPhotosWithExtraData) => {
      state.singleWithExtraData = payload;
    },
  },

  actions: {
    ...base.actions,

    getWithTagsAndTranslations: async (
      ctx: IAction<IStateStrict<ISuitesHasPhotos>>,
      payload: IFilterPayloadV2<number>,
    ) => {
      await ctx.dispatch('get', {
        ...payload,
        ...{ additional: { includeTranslations: true } },
      } as IFilterPayloadV2<number>);

      const tagsResponse = await getByModelAndObjectId('unit-photos', ctx.getters.single.id);
      ctx.commit('setSingleWithExtraData', { ...ctx.getters.single, ...{ objectsHasTags: tagsResponse.data || [] } });
    },

    insertOne: async (
      ctx: IAction<IStateStrict<ISuitesHasPhotos>>,
      payload: IModifyPayload<number, ISuitesHasPhotos>,
    ): Promise<ISuitesHasPhotos | undefined> => {
      if (!payload.data.listingPhoto) return undefined;

      const unitPhotoResponse: IApiResponse<ISuitesHasPhotos[], null> = await adminRequest.post(
        `/property-api/units/${payload.parentId}/assigned-photos`,
        subsetObject(getNewSuitesHasPhotos(payload.data.listingPhoto, payload.data.orderBy, payload.parentId!), ['listingPhoto']),
      );

      if (!unitPhotoResponse) return undefined;

      const suitsHasPhoto: ISuitesHasPhotos = { ...unitPhotoResponse.data![0]!, listingPhoto: payload.data.listingPhoto };
      ctx.commit('set', [...ctx.getters.list, suitsHasPhoto ]);

      return suitsHasPhoto;
    },

    uploadAndSave: async (
      ctx: IAction<IStateStrict<ISuitesHasPhotos>>,
      payload: IModifyPayload<number, File, { orderBy: number, clientKey: string, clientId: number }>,
    ): Promise<ISuitesHasPhotos | undefined> => {
      const uploadResult = await uploads.uploadSingleFile(
        payload.additional!.clientKey,
        'gallery',
        payload.data,
      );

      const image = uploadResult.filename.split('/').pop()!;

      const listingPhotoResponse: IApiResponse<IListingPhotos[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/listing-photos`,
        getNewListingPhoto(payload.additional!.clientId, payload.parentId!, image, uploadResult.height, uploadResult.width),
      );
      if (!listingPhotoResponse) return undefined;

      const UnitPhotoResponse: IApiResponse<ISuitesHasPhotos[], null> = await adminRequest.post(
        `/property-api/units/${payload.id}/assigned-photos`,
        subsetObject(getNewSuitesHasPhotos(listingPhotoResponse.data![0]!, payload.additional!.orderBy, payload.id || -1), ['listingPhoto']),
      );

      if (!UnitPhotoResponse) return undefined;

      const suitsHasPhoto: ISuitesHasPhotos = { ...UnitPhotoResponse.data![0]!, listingPhoto: listingPhotoResponse.data![0]! };
      ctx.commit('set', [...ctx.getters.list, suitsHasPhoto ]);

      return suitsHasPhoto;
    },

    saveUploadedImageEdits: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, IListingPhotos, { clientKey: string, filename: string, imageEdits: IEditImage[] }>,
    ): Promise<IListingPhotos> => {
      const uploadEditResult = await uploads.updateImageAsNew(
        payload.additional!.clientKey,
        payload.additional!.filename,
        'gallery',
        payload.additional!.imageEdits,
      );

      const updatedPhoto: IApiResponse<IListingPhotos, null> = await adminRequest.put(
        `/property-api/units/${payload.parentId}/photos/${payload.id}`,
        {
          image: uploadEditResult.filename.split('/').pop(),
          ...!payload.data.originalImage && { originalImage: payload.data.image },
        },
      );

      const newPhoto = { ...ctx.getters.single, ...updatedPhoto.data! };
      ctx.commit('set', newPhoto);
      return newPhoto;
    },

    resetToOriginalImage: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, IListingPhotos>,
    ): Promise<IListingPhotos> => {
      const updatedPhoto: IApiResponse<IListingPhotos, null> = await adminRequest.put(
        `/property-api/units/${payload.parentId}/photos/${payload.id}`,
        { image: payload.data.originalImage },
      );

      const newPhoto = { ...ctx.getters.single, ...updatedPhoto.data! };
      ctx.commit('set', newPhoto);
      return newPhoto;
    },

  },
};
